import useAppSelector from '@/features/app/hooks/useAppSelector';
import useCartQuery from '@/features/cart/queries/useCartQuery';
import { TireSize } from '@/features/products/types/arktype/TireSize';
import { useCallback, useMemo } from 'react';

export default function useCartDisplayEntries(filter?: {
  sort?: 'oldest' | 'latest';
  search?: string;
}) {
  const { sort = 'latest', search = '' } = filter ?? {};
  const { user } = useAppSelector((state) => state.auth);
  const { data: userCarts } = useCartQuery({
    enabled: user?.__role === 'ROLE_WEBSHOP_USER',
  });

  const matchTireSizeWithSearch = useCallback(
    (tireSize: Omit<TireSize, 'itemPromotions'>, searchString: string) => {
      const description = `${tireSize?.tire?.sizeDescription} ${tireSize?.speedRating2 ?? tireSize?.speedRating}${
        tireSize?.loadCapacity ? ' ' + tireSize.loadCapacity : ''
      } ${tireSize?.tire?.frFlag ? ' ' + tireSize.tire?.frFlag : ''}`;
      const cleanedDescription =
        `${tireSize?.tire?.sizeDescription?.replace(/[A-Za-z/. ]/g, '')}${tireSize?.loadRating}${tireSize?.speedRating}`.toLowerCase();

      return (
        description.toLowerCase().includes(searchString.toLowerCase()) ||
        cleanedDescription.includes(searchString.toLowerCase()) ||
        tireSize?.tire.title?.toLowerCase().includes(searchString.toLowerCase()) ||
        tireSize?.tire.brand?.brandName?.toLowerCase().includes(searchString.toLowerCase())
      );
    },
    []
  );

  return useMemo(() => {
    const cartItems = (userCarts?.cartItem ?? []).filter((item) => {
      if (item.type === 'productBundle') {
        return item.productBundle.productBundleItem.some((item) =>
          matchTireSizeWithSearch(item.tireSize, search)
        );
      } else {
        return matchTireSizeWithSearch(item.tireSize!, search);
      }
    });
    if (sort === 'latest') cartItems.reverse();
    return cartItems;
  }, [matchTireSizeWithSearch, search, sort, userCarts]);
}
